// push.ts — turning notifications on for this device.
//
// Three things have to line up, and each fails differently, so each says so
// differently: the browser must support push at all, the server must have VAPID
// configured, and the person must grant permission. "Notifications unavailable"
// would send somebody to their phone settings for a server problem.
//
// A DEVICE, not an account. The subscription belongs to this browser on this
// machine — turning it on here says nothing about the person's other devices,
// which is why the server stores one row per endpoint.
import { loadRuntimeConfig } from '../runtime-config'

export type PushState =
  | { status: 'unsupported'; reason: string }
  | { status: 'not-configured'; reason: string }
  | { status: 'denied'; reason: string }
  | { status: 'off' }
  | { status: 'on' }

export interface PushDeps {
  fetchImpl?: typeof fetch
  baseUrl?: string | (() => string | Promise<string>)
}

/** VAPID keys travel as base64url; `applicationServerKey` wants raw bytes. */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  // The ArrayBuffer, not the view: TypeScript's BufferSource does not accept a
  // Uint8Array whose backing buffer type is unresolved, and the DOM wants the
  // bytes either way.
  return out.buffer
}

function supported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function createPushClient(deps: PushDeps = {}) {
  const doFetch = deps.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args))

  async function base(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  async function serverKey(): Promise<{ ok: true; key: string } | { ok: false; reason: string }> {
    const res = await doFetch(`${await base()}/ops/push/key`, { credentials: 'include' })
    if (!res.ok) return { ok: false, reason: `The server refused the notification key (HTTP ${res.status}).` }
    const body = (await res.json()) as { configured?: boolean; publicKey?: string; reason?: string }
    if (body.configured !== true || !body.publicKey) {
      return { ok: false, reason: body.reason ?? 'Push is not configured on this deployment.' }
    }
    return { ok: true, key: body.publicKey }
  }

  /** What this device's state is right now, without changing anything. */
  async function current(): Promise<PushState> {
    if (!supported()) {
      // Being specific: on an iPhone this is almost always "you are in Safari
      // rather than the installed app", which is a thing the person can fix.
      return {
        status: 'unsupported',
        reason:
          'This browser cannot receive notifications. On an iPhone they only work once the app is added to the Home Screen and opened from there.',
      }
    }
    if (Notification.permission === 'denied') {
      return {
        status: 'denied',
        reason: 'Notifications are blocked for this site in your browser settings, so they cannot be switched on here.',
      }
    }
    const registration = await navigator.serviceWorker.getRegistration()
    const existing = await registration?.pushManager.getSubscription()
    return existing ? { status: 'on' } : { status: 'off' }
  }

  /** Register, subscribe, and tell the server. Returns the resulting state. */
  async function enable(): Promise<PushState> {
    const state = await current()
    if (state.status === 'unsupported' || state.status === 'denied' || state.status === 'on') return state

    const key = await serverKey()
    if (!key.ok) return { status: 'not-configured', reason: key.reason }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return {
        status: 'denied',
        reason: 'Notifications were not allowed, so nothing will be sent to this device.',
      }
    }

    const registration = await navigator.serviceWorker.register('/sw.js')
    // `ready` rather than the registration object: a worker that is installing
    // cannot take a subscription yet.
    await navigator.serviceWorker.ready

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key.key),
    })

    const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
    const res = await doFetch(`${await base()}/ops/push/subscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        endpoint: json.endpoint,
        kind: 'web',
        keys: json.keys,
        // Just enough to tell two devices apart in a list later.
        label: navigator.userAgent.slice(0, 100),
      }),
    })
    if (!res.ok) {
      // Rolled back rather than left half-on: a browser subscription the server
      // does not know about is a device that believes it is covered and is not.
      await subscription.unsubscribe().catch(() => {})
      return { status: 'not-configured', reason: `The server would not store this device (HTTP ${res.status}).` }
    }
    return { status: 'on' }
  }

  async function disable(): Promise<PushState> {
    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = await registration?.pushManager.getSubscription()
    if (!subscription) return { status: 'off' }

    // Told to the server FIRST: if the browser forgets the subscription and
    // the server keeps it, we page an endpoint nobody is listening to and
    // count somebody as reachable who is not.
    await doFetch(`${await base()}/ops/push/unsubscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    }).catch(() => {})

    await subscription.unsubscribe().catch(() => {})
    return { status: 'off' }
  }

  return { current, enable, disable }
}

export const push = createPushClient()
